import { differenceInDays } from 'date-fns';
import { OngoingIncidentsResponse } from '../types';
import { transformKeysFromSnakeCaseToCamelCase } from '@internal/ui-common';
import {
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  reportDatadog,
  reportDatadogAction,
} from '../../../../Root/hooks/reportDatadog';
import { useCallback, useEffect, useState } from 'react';

export const useOngoingIncidents = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const errorApi = useApi(errorApiRef);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [ongoingIncidents, setOngoingIncidents] =
    useState<OngoingIncidentsResponse>();
  const [daysWithoutIncidents, setDaysWithoutIncidents] = useState<number>(0);
  const [lastUpdateTime, setLastUpdateTime] = useState<string | null>(null);

  const fetchOngoingIncidents = useCallback(async () => {
    setError(null);
    let endpoint = '';
    const startTime = performance.now();

    try {
      const apiUrl = await discoveryApi.getBaseUrl('proxy');
      const apiSuffix = '/ihp/CurrentIncidents';
      endpoint = `${apiUrl}${apiSuffix}`;

      const response = await fetchApi.fetch(endpoint);

      reportDatadogAction(startTime, endpoint, response);
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      const transformedData: OngoingIncidentsResponse =
        transformKeysFromSnakeCaseToCamelCase(data);

      setOngoingIncidents(transformedData);
      setLastUpdateTime(
        new Date().toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      );
      setDaysWithoutIncidents(
        differenceInDays(
          new Date(),
          new Date(transformedData.lastIncidentDate),
        ),
      );
    } catch (e) {
      const errorObject = new Error('Failed to fetch ongoing incidents data.', {
        cause: e,
      });
      await reportDatadog(startTime, endpoint, errorObject);

      errorApi.post(errorObject);
      setError(errorObject);
    } finally {
      setIsLoading(false);
    }
  }, [discoveryApi, errorApi, fetchApi]);

  useEffect(() => {
    fetchOngoingIncidents();
    const interval = setInterval(fetchOngoingIncidents, 2 * 60 * 1000); // 2 minutes
    return () => clearInterval(interval);
  }, [fetchOngoingIncidents]);

  return {
    isLoading,
    ongoingIncidents,
    error,
    lastUpdateTime,
    daysWithoutIncidents,
  };
};
